import GoogleTagManager from "@redux-beacon/google-tag-manager";
import { List } from "linqts";
import { createMiddleware } from "redux-beacon";

import { RootState } from "../store/store";
import { BookingInfo, ServiceProgram } from "../types/models";
import { BookingPreview, IBookingProgram, Roll, Service, ViewItemListItem } from "../types/types";

const gtm = GoogleTagManager(window.__config.GTM_CONTAINER);
const serviceProgramListEvent = (action: any, _: any, state: RootState) => [
  {
    hitType: "view_item_list",
    ecommerce: {
      items: action.payload.map((p: ServiceProgram, index: number) =>
        toGaListItem(p, index, state.session.selectedEventType)
      ),
      item_list_id: "related_products",
      item_list_name: "Related products",
    },
  },
  {
    hitType: "select_item",
    ecommerce: {
      items: action.payload.map((p: ServiceProgram, index: number) => toGaListItem(p, index)),
      item_list_id: "select_item",
      item_list_name: "Select item",
    },
  },
];

const serviceProgramListFilter = (action: any, _: any, state: RootState) => [
  {
    hitType: "view_item_list",
    ecommerce: {
      items: state.servicePrograms.servicePrograms
        ?.filter((p) => action.payload === "All" || p.session.programActivityType === action.payload)
        .map((p: ServiceProgram, index: number) => toGaListItem(p, index, state.session.selectedEventType)),
      item_list_id: "related_products",
      item_list_name: "Related products",
    },
  },
];

const serviceProgramBeginCheckout = (action: any) => ({
  hitType: "begin_checkout",
  ecommerce: {
    items: action.payload.map((p: ServiceProgram, index: number) => toGaListItem(p, index)),
  },
});

const serviceRocketeersProgramBeginCheckout = (action: any) => ({
  hitType: "begin_checkout",
  ecommerce: {
    items: action.payload.map((p: IBookingProgram, index: number) => bookingPprogramToGaListItem(p, index)),
  },
});

const serviceProgramSelect = (action: any) => ({
  hitType: action.payload.event,
  ecommerce: {
    items: [toGaListItem(action.payload.serviceProgram, 0)],
  },
});

const serviceProgramView = (action: any) => [
  {
    hitType: "view_item",
    ecommerce: {
      items: [
        {
          item_name: "Vacation care",
          item_id: `${action.payload.serviceProgram.rollId}`,
          price: action.payload.serviceProgram.session.primaryFeeAmount,
          item_brand: action.payload.serviceProgram.serviceName,
          item_category: action.payload.serviceProgram.rollName,
          item_category2: action.payload.serviceProgram.session.programActivityType,
          item_category4: `${action.payload.serviceProgram.season} ${action.payload.serviceProgram.year}`,
          item_variant: action.payload.serviceProgram.programName,
          quantity: 1,
        },
      ],
    },
  },
];

function bookingComplete(action: any, _: any, state: RootState): any {
  let total = 0;

  if (state.bookingSaveCasualPreview?.previewResult) {
    state.bookingSaveCasualPreview.previewResult.forEach((b: BookingPreview) => total + b.fee);
  }

  return {
    hitType: "purchase",
    ecommerce: {
      value: total,
      currency: "AUD",
      transaction_id: action.payload.reference,
      items: action.payload.programs.map((p: ServiceProgram, index: number) => toGaListItem(p, index)),
    },
  };
}

function loadedSavedBookings(action: any, _: any, state: RootState): any {
  if (action.payload.bookings.length) {
    let items = action.payload.bookings.map((b: BookingInfo) => ({
      item_id: b.rollId,
      item_name: "OSHC",
      affiliation: b.date.toISOString(),
      item_brand: b.roll?.service?.serviceName,
      item_category: b.roll?.rollName,
      item_category2: b.roll?.sessionType,
      item_category3: "non-holiday booking",
      item_category4: b.date.toISOString(),
      item_list_id: b.uniqueId,
      item_list_name: b.roll?.rollName,
      item_variant: b.roll?.sessionType,
      price: state.booking.isEditingCasual ? b.roll?.casualFeeAmount ?? 0 : b.roll?.primaryFeeAmount ?? 0,
      quantity: 1,
      index: 0,
    }));

    return {
      hitType: "purchase",
      ecommerce: {
        value: new List(items).Sum((b: any) => b.price),
        currency: "AUD",
        transaction_id: action.payload.bookings[0].uniqueId,
        items: items,
      },
    };
  }

  return null;
}

function SelectOshBookingStep(action: any, _: any, state: RootState): any {
  if (action.payload === 1) {
    const services = new Map();

    state.services.servicesByCustomer?.forEach((s) => {
      services.set(s.serviceId, s);
    });

    let listItems = mapRollsToListItems(state.session.rolls?.filter((r) => r.isOhsc) ?? [], state);

    if (listItems) {
      return [
        {
          hitType: "view_item_list",
          item_list_id: "related_products",
          item_list_name: "Related products",
          ecommerce: {
            items: listItems,
          },
        },
        {
          hitType: "select_item",
          ecommerce: {
            items: listItems,
            item_list_id: "select_item",
            item_list_name: "Select item",
          },
        },
      ];
    }
  }

  return null;
}

function RemoveOshBookingSelection(action: any, _: any, state: RootState) {
  const serviceMap = buildServiceMap(state);
  const removedRoll: Roll = action.payload;

  const removedItem = {
    item_name: "OSHC",
    item_id: removedRoll.rollId,
    price: removedRoll.primaryFeeAmount,
    item_brand: serviceMap?.get(removedRoll.serviceId)?.serviceName,
    item_category: removedRoll.rollName,
    item_category2: removedRoll.sessionType,
    item_category3: "non-holiday booking",
    item_variant: serviceMap?.get(removedRoll.serviceId)?.serviceName,
    quantity: 1,
  };

  return {
    hitType: "remove_from_cart",
    items: [removedItem],
  };
}

function AddOshBookingSelection(action: any, _: any, state: RootState) {
  const serviceMap = buildServiceMap(state);
  const addedRoll: Roll = action.payload;

  const addedItem = {
    item_name: "OSHC",
    item_id: addedRoll.rollId,
    price: addedRoll.primaryFeeAmount,
    item_brand: serviceMap?.get(addedRoll.serviceId)?.serviceName,
    item_category: addedRoll.rollName,
    item_category2: addedRoll.sessionType,
    item_category3: "non-holiday booking",
    item_category4: state.session.startDate?.format("D MMMM YYYY"),
    item_variant: serviceMap?.get(addedRoll.serviceId)?.serviceName,
    quantity: 1,
  };

  return [
    {
      hitType: "view_item",
      items: [addedItem],
    },
    {
      hitType: "begin_checkout",
      items: [addedItem],
    },
    {
      hitType: "add_to_cart",
      items: [addedItem],
    },
  ];
}

function buildServiceMap(state: RootState): Map<number, Service> {
  const services = new Map();

  state.services.servicesByCustomer?.forEach((s) => {
    services.set(s.serviceId, s);
  });

  return services;
}

function mapRollsToListItems(rolls: Array<Roll>, state: RootState) {
  let serviceMap = buildServiceMap(state);

  state.services.servicesByCustomer?.forEach((s) => {
    serviceMap.set(s.serviceId, s);
  });

  return rolls.map((r, i) => ({
    item_id: r.rollId,
    item_name: "OSHC",
    affiliation: state.session.startDate?.toISOString(),
    item_brand: serviceMap?.get(r.serviceId)?.serviceName,
    item_category: r.rollName,
    item_category2: r.sessionType,
    item_category3: "non-holiday booking",
    item_category4: state.session.startDate?.format("D MMMM YYYY"),
    item_list_id: r.rollId,
    item_list_name: r.primaryFeeName,
    price: r.primaryFeeAmount,
    quantity: 1,
    index: i,
  }));
}

function bookingScheduleCancelled(action: any, _: any, state: RootState): any {
  let b: BookingInfo | undefined | null = state.booking.bookings?.find((b) => b.schedule?.uniqueId === action.payload);

  if (!b) {
    b = state.session.selectedBooking;
  }

  if (b) {
    return toRefundGaItem(b, state);
  }
}

function bookingInividualCancelled(action: any, _: any, state: RootState): any {
  let b: BookingInfo | undefined | null = state.booking.bookings?.find((b) => b.schedule?.uniqueId === action.payload);

  if (!b) {
    b = state.session.selectedBooking;
  }

  if (b) {
    return toRefundGaItem(b, state);
  }
}

function toRefundGaItem(b: BookingInfo, state: RootState) {
  return {
    hitType: "refund",
    ecommerce: {
      items: [
        {
          item_name: b.program?.season ? "Vacation care" : "OSHC",
          item_id: `${b.program?.sessionId ?? b.roll?.rollId}`,
          price: b.feeAmount ?? b.roll?.primaryFeeAmount,
          item_brand: b.roll?.service?.serviceName,
          item_category: b.roll?.rollName,
          item_category2: b.program?.activityCode ?? b.roll?.sessionType,
          item_category4: `${b.program?.season ?? b.roll?.rollName} ${
            b.program?.year ?? state.session.startDate?.format("D MMMM YYYY")
          }`,
          item_variant: b.program?.programName ?? b.roll?.primaryFeeName,
          quantity: b.schedule?.numberOfUpcomingBookings ?? 1,
        },
      ],
    },
  };
}

function toGaListItem(p: ServiceProgram, index: Number, filter: String | null = null): ViewItemListItem {
  return {
    item_id: `${p.rollId}`,
    item_name: "Vacation care",
    affiliation: p.date.toISOString(),
    item_brand: p.serviceName,
    item_category: p.rollName,
    item_category2: p.session.programActivityType,
    item_category4: p.date.format("D MMMM YYYY"),
    item_variant: `${p.programName}`,
    item_list_name: `${filter}`,
    item_list_id: `${filter}`,
    price: p.session.primaryFeeAmount,
    quantity: 1,
    index,
  };
}

function bookingPprogramToGaListItem(
  p: IBookingProgram,
  index: Number,
  filter: String | null = null
): ViewItemListItem {
  return {
    item_id: `${p.rollId}`,
    item_name: "Vacation care",
    affiliation: p.date.toISOString(),
    item_brand: p.serviceName ?? "",
    item_category: p.rollName,
    item_category2: p.isNationalProgram ? p.classification : p.session.programActivityType,
    item_category4: p.date.format("D MMMM YYYY"),
    item_variant: `${p.programName}`,
    item_list_name: `${filter}`,
    item_list_id: `${filter}`,
    price: p.session.primaryFeeAmount,
    quantity: 1,
    index,
  };
}

function saveRecurringBookingSuccess(action: any, _: any, state: RootState): any {
  const services = new Map();
  const serviceMap = buildServiceMap(state);

  state.services.servicesByCustomer?.forEach((s) => {
    services.set(s.serviceId, s);
  });

  let total = 0;

  if (state.recurringBookingPreview?.bookings) {
    state.recurringBookingPreview?.bookings.forEach((b: BookingPreview) => (total += b.fee));
  }

  let rollIds = state.recurringBookingPreview?.bookings.map((b) => b.rollId);
  let rolls = state.session.rolls?.filter((r) => rollIds.includes(r.rollId));

  var items = rolls?.map((r, i) => ({
    item_id: r.rollId,
    item_name: "OSHC",
    affiliation: state.session.startDate?.toISOString(),
    item_brand: serviceMap?.get(r.serviceId)?.serviceName,
    item_category: r.rollName,
    item_category2: r.sessionType,
    item_category4: state.session.startDate?.format("D MMMM YYYY"),
    item_list_id: r.rollId,
    item_list_name: r.primaryFeeName,
    price: r.primaryFeeAmount,
    quantity: 1,
    index: i,
  }));

  return {
    hitType: "purchase",
    ecommerce: {
      value: total,
      currency: "AUD",
      transaction_id: action.payload.reference,
      items: items,
    },
  };
}

const eventsMap = {
  serviceProgramsView: serviceProgramListEvent,
  serviceProgramSelect: serviceProgramSelect,
  serviceProgramBeginCheckout: serviceProgramBeginCheckout,
  SaveVacBookingComplete: bookingComplete,
  BookingScheduleCancelled: bookingScheduleCancelled,
  BookingIndividualCancelled: bookingInividualCancelled,
  serviceProgramView: serviceProgramView,
  LoadedSavedBookings: loadedSavedBookings,
  SelectOshBookingStep: SelectOshBookingStep,
  RemoveOshBookingSelection: RemoveOshBookingSelection,
  AddOshBookingSelection: AddOshBookingSelection,
  SelectEventType: serviceProgramListFilter,
  SaveRecurringBookingSuccess: saveRecurringBookingSuccess,
  serviceRocketeersProgramBeginCheckout: serviceRocketeersProgramBeginCheckout,
};

export const gtmMiddleware = createMiddleware(eventsMap, gtm);
